/**
 * Houseplant v2 — Klaviyo newsletter signup
 *
 * PROGRESSIVE ENHANCEMENT over the Shopify `{% form 'customer' %}` in the footer.
 * The Liquid form is the real, working fallback: with no Klaviyo credentials
 * configured, with JS off, or if Klaviyo is unreachable, the form posts to
 * Shopify exactly as it did before and the subscriber lands in Customers. This
 * only takes over the submit when it can actually do better.
 *
 * WHY BOTH. Klaviyo's own Shopify integration already syncs Shopify customers,
 * so the plain form does eventually reach Klaviyo — on Klaviyo's sync schedule
 * and into whatever list the integration is mapped to. Posting directly puts the
 * profile on a NAMED list immediately, which is what flows and welcome series
 * key off. Losing the Shopify record entirely would be worse, so this posts to
 * BOTH — Klaviyo first, then Shopify in the background.
 *
 * ── NOTHING HERE NAVIGATES ───────────────────────────────────────────────
 * Both posts are `fetch`, and the result is written into the live region. That
 * is a behaviour change and it fixed a reported bug, described in full on
 * `onSubmit` below: the Shopify hand-off used to be a real form submission, so
 * every signup reloaded the whole site, and it reloaded onto a FALSE error.
 *
 * ── Credentials ──────────────────────────────────────────────────────────────
 * `company_id` is Klaviyo's PUBLIC API key (Site ID) — a six-character public
 * identifier that is safe in page source; it is what every Klaviyo embed form
 * ships with. A PRIVATE key must never appear here, and the settings help text
 * says so. Both values come from theme settings, so a merchant can point the
 * form at a different list without a deploy.
 *
 * Endpoint contract (verified against Klaviyo's Create Client Subscription docs):
 *   POST https://a.klaviyo.com/client/subscriptions?company_id=<public key>
 *   revision: 2026-07-15
 *   content-type: application/vnd.api+json
 *   → 202 Accepted on success
 */
const HP_KLAVIYO_ENDPOINT = 'https://a.klaviyo.com/client/subscriptions';
const HP_KLAVIYO_REVISION = '2026-07-15';

class HpNewsletter extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.input = this.querySelector('input[type="email"]');
    this.status = this.querySelector('.hp-newsletter__status');

    this.companyId = this.dataset.klaviyoPublicKey || '';
    this.listId = this.dataset.klaviyoListId || '';
    this.customSource = this.dataset.klaviyoSource || 'Footer Newsletter';

    // Not configured — leave the native Shopify form completely alone.
    if (!this.form || !this.input || !this.companyId || !this.listId) return;

    /**
     * `connectedCallback` RUNS AGAIN EVERY TIME THE ELEMENT IS RE-INSERTED, and
     * nothing here removes the listener, so a second run would bind a second
     * one — and every signup would be submitted TWICE, to both Klaviyo and
     * Shopify. Caught while testing this file: moving the element in the DOM
     * reproduced it immediately (two Klaviyo posts, two customer posts, from one
     * Enter press). Nothing in the theme moves the footer today; the guard costs
     * a line and the failure mode is duplicate profiles in a live audience. */
    if (this.bound) return;
    this.bound = true;

    /**
     * ── SHOPIFY'S CAPTCHA SCRIPT WAS RE-SUBMITTING THIS FORM NATIVELY ────────
     * This is the cause of the "reloads with 'Email cannot be blank' while the
     * profile IS in Klaviyo" bug, and it is invisible locally: the storefront
     * captcha bootstrap only runs on a real storefront, never on a dev theme.
     *
     * That script (`ce_forms` / storefront-forms-hcaptcha) listens for `submit`
     * ON THE DOCUMENT and claims any form matching
     *
     *   form[action*='/contact']:not([data-nocaptcha='true'])
     *     input[name='form_type'][value='customer']
     *
     * which is exactly what `{% form 'customer' %}` renders here. On a claimed
     * form with no `form_key` yet it persists the fields to sessionStorage and
     * calls `form.submit()` — a RAW programmatic submit. That bypasses our
     * `preventDefault()` (it is a fresh navigation, not this event), bypasses
     * the input's `required` (programmatic submits skip constraint validation),
     * and tears down the page mid-request, which is why the only console
     * evidence was a `NetworkError` on our background fetch.
     *
     * `data-nocaptcha="true"` is that script's own opt-out, read from its
     * source rather than from documentation — so treat it as a known-good hook
     * that could change, not as a contract.
     *
     * SET FROM JS, NOT LIQUID, and not by choice: `{% form %}` passes extra
     * attributes through, but Liquid argument names cannot contain a hyphen, so
     * `data-nocaptcha` cannot be written as a tag argument at all. Same
     * limitation the PDP gallery hit with `aria-label` on `media_tag`.
     *
     * SET ONLY ON THIS BRANCH — i.e. only once we know Klaviyo is configured and
     * we are taking over the submit. If the element is unconfigured we return
     * above, the native Shopify form is what actually posts, and it KEEPS
     * Shopify's spam protection.
     */
    this.form.setAttribute('data-nocaptcha', 'true');

    this.form.addEventListener('submit', this.onSubmit.bind(this));
  }

  /**
   * ── THE BUG THIS METHOD WAS REPORTED FOR ──────────────────────────────────
   * "Submitting the footer newsletter reloads the whole site and shows
   * 'Email cannot be blank' in red — but the profile IS in Klaviyo."
   *
   * Both halves had one cause. The Shopify hand-off ran as a REAL form
   * submission, and it ran AFTER the input had been cleared:
   *
   *     this.input.value = '';
   *     HTMLFormElement.prototype.submit.call(this.form);
   *
   * `submit()` serialises the form's CURRENT values, so Shopify received
   * `contact[email]=""` and answered with its own "Email cannot be blank" —
   * about a field the visitor had filled in correctly. `submit()` also bypasses
   * constraint validation (that is exactly where it differs from
   * `requestSubmit()`), so the `required` attribute could not catch the empty
   * value either. Klaviyo had already been told, which is why the profile was on
   * the list while the screen said the opposite.
   *
   * The navigation was the other half: it wiped the live region a few
   * milliseconds after it was written, so the success message could never be
   * read and a full page load was the only feedback anyone ever saw.
   *
   * BOTH POSTS ARE NOW `fetch`, AND THE PAYLOAD IS CAPTURED BEFORE ANYTHING IS
   * CLEARED. The order in here is load-bearing — read `payload` before
   * `input.value = ''`, or this bug comes straight back.
   */
  async onSubmit(event) {
    const email = this.input.value.trim();
    if (!email) return; // let the browser's own `required` validation speak

    event.preventDefault();
    this.setBusy(true);
    this.announce('');

    /**
     * CAPTURED FIRST, while the field still holds the address. FormData
     * snapshots the controls' values at construction, so it survives the reset
     * further down.
     */
    const payload = new FormData(this.form);

    try {
      const response = await fetch(
        `${HP_KLAVIYO_ENDPOINT}?company_id=${encodeURIComponent(this.companyId)}`,
        {
          method: 'POST',
          headers: {
            revision: HP_KLAVIYO_REVISION,
            'content-type': 'application/vnd.api+json',
          },
          body: JSON.stringify({
            data: {
              type: 'subscription',
              attributes: {
                custom_source: this.customSource,
                profile: {
                  data: {
                    type: 'profile',
                    attributes: {
                      email,
                      subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } },
                    },
                  },
                },
              },
              relationships: { list: { data: { type: 'list', id: this.listId } } },
            },
          }),
        }
      );

      if (response.status !== 202) throw new Error(`Klaviyo responded ${response.status}`);

      /**
       * Klaviyo is the system the signup is FOR, so its 202 is the answer the
       * visitor gets — immediately, without waiting on Shopify. The customer
       * record is secondary bookkeeping; if it fails the person is still
       * subscribed, and telling them otherwise would be wrong.
       */
      this.announce(this.dataset.successMessage || 'Thanks for subscribing.', 'success');
      this.input.value = '';

      if (this.dataset.alsoPostToShopify === 'true') {
        this.postToShopify(payload).catch((error) => {
          console.warn('[hp-newsletter] Klaviyo succeeded but the Shopify customer record failed:', error);
        });
      }
    } catch (error) {
      /**
       * FAIL OVER, NOT OPEN. A network error, an ad blocker, a misconfigured
       * list — none of those should cost the signup, so Shopify still gets it.
       * What changed is that we now read Shopify's ACTUAL verdict and say it
       * here, instead of navigating and letting the reloaded page speak.
       */
      console.warn('[hp-newsletter] Klaviyo subscribe failed, falling back to Shopify:', error);

      try {
        const result = await this.postToShopify(payload);
        if (result.ok) {
          this.announce(this.dataset.successMessage || 'Thanks for subscribing.', 'success');
          this.input.value = '';
        } else {
          this.announce(result.message || 'Something went wrong. Please try again.');
        }
      } catch (fallbackError) {
        /**
         * BOTH ENDPOINTS UNREACHABLE. This used to hand off to a native
         * `requestSubmit()` — the reasoning being that the server's own page
         * beats a dead form. That is now deliberately NOT done, for two reasons.
         *
         * First, the requirement is that this control never reloads the site,
         * and a path that reloads "only when things go wrong" is exactly the
         * path a visitor hits on a flaky connection. Second, a native submit is
         * unverifiable from here: it navigates away, so whatever it produces
         * cannot be reported, corrected or logged — which is how a false
         * "Email cannot be blank" was able to sit on screen while the signup had
         * in fact succeeded.
         *
         * The field is deliberately left populated, so "try again" is one press
         * of Enter rather than a retype.
         */
        console.warn('[hp-newsletter] Both Klaviyo and Shopify were unreachable:', fallbackError);
        this.announce('Sorry — we could not reach the server. Please try again.');
      }
    } finally {
      this.setBusy(false);
    }
  }

  /**
   * POST the captured FormData to the Shopify form's own action, without
   * navigating.
   *
   * Shopify answers a customer form with the WHOLE PAGE — there is no JSON
   * endpoint for it — so the verdict has to be read out of the returned markup.
   * The node it looks for is one this theme renders itself
   * (`.form__message.errors` inside the form), so this is reading our own Liquid
   * rather than guessing at Shopify's.
   */
  async postToShopify(payload) {
    const response = await fetch(this.form.action, {
      method: 'POST',
      body: payload,
      headers: { Accept: 'text/html' },
    });

    if (!response.ok) throw new Error(`Shopify responded ${response.status}`);

    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    // Scoped to this form, so another form's message on the same page can't be read.
    const scope = (this.form.id && doc.getElementById(this.form.id)) || doc;

    const error = scope.querySelector('.form__message.errors');
    if (error) return { ok: false, message: error.textContent.trim() };

    return { ok: true };
  }

  setBusy(busy) {
    this.toggleAttribute('data-busy', busy);
    const button = this.querySelector('button[type="submit"]');
    if (button) button.disabled = busy;
  }

  /** The status node is `aria-live`, so assistive tech hears the result. */
  announce(message, kind) {
    if (!this.status) return;
    this.status.textContent = message;
    this.status.classList.toggle('hp-newsletter__status--success', kind === 'success');
  }
}

if (!customElements.get('hp-newsletter')) {
  customElements.define('hp-newsletter', HpNewsletter);
}
